/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

// hooks/useSearchAggregator.ts
import { useAppConfig } from '@airasia-rn/ui';
import { useFlagEnabled as useIsFlagEnabled } from '@airasia-rn/account';
import { useLogger } from '@airasia-rn/core-logger';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Platform } from 'react-native';

import {
  apiErrorMeta,
  classifyServiceError,
  errorTrackingEvent,
} from '../analytics';
import type { Environment } from '../services/funnelControlApiService';
import { FunnelControlApiService } from '../services/funnelControlApiService';
import { SearchAggregatorApiService } from '../services/searchAggregatorApiService';

import { useFlightConfig } from '../config/FlightConfigProvider';
import { flightKeys, SearchFlightFilters } from './flights';
import { getProvider } from '../util/FlightSearch.utils';
import {
  buildAggregatorBody,
  buildAggregatorFilterParams,
  buildAggregatorQueryParams,
} from '../util/searchAggregatorRequest.utils';

import type {
  FunnelControlRequest,
  FunnelControlResponse,
  FunnelHeadersInput,
  PipelineResult,
  SearchAggregatorInput,
  SearchAggregatorVars,
  SSODetails,
  UserContext,
} from '../types/FlightSearch.types';

import {
  useAccessToken,
  useIsLoggedIn,
  useRefreshToken,
  useUserId,
} from '@airasia-rn/account/hooks';
import { TripType } from '../types/FlightSearch.types';
import {
  formatDateDDMMYYYY,
  getBooleanQueryParam,
  getApiPlatform,
  hasValidAnonymousSso,
  getStringQueryParam,
} from '../util/utils';
import { useAuthToken } from './useAuth';
import { useMultiCartContext } from './useMultiCartContext';

export type SearchAggregatorState =
  | { status: 'idle'; data: null; error: null }
  | { status: 'loading'; data: PartialSearchData | null; error: null }
  | { status: 'success'; data: PipelineResult; error: null }
  | { status: 'error'; data: PartialSearchData | null; error: unknown };

type PartialSearchData = {
  funnel: FunnelControlResponse | null;
  aggregator: PipelineResult['aggregator'] | null;
};

export const useSearchAggregatorSelector = () => {
  const { data } = useSearchAggregatorResult();

  return {
    funnel: data?.status === 'success' ? data.data.funnel : null,
    searchAgg: data?.status === 'success' ? data.data.aggregator : null,

    isIdle: data?.status === 'idle',
    isLoading: data?.status === 'loading',
    isSuccess: data?.status === 'success',
    isError: data?.status === 'error',

    error: data?.status === 'error' ? data.error : null,
  };
};

export const useSearchAggregatorResult = () =>
  useQuery<SearchAggregatorState>({
    queryKey: flightKeys.search,
    queryFn: async () =>
      ({ status: 'idle', data: null, error: null }) as SearchAggregatorState,
    enabled: false, // cache only
    initialData: { status: 'idle', data: null, error: null },
    staleTime: Infinity,
  });

export const useSearchAggregator = (screenName: string = getApiPlatform()) => {
  const appConfig = useAppConfig();
  const flightConfig = useFlightConfig();
  const queryClient = useQueryClient();
  const { logError } = useLogger();

  // Moved inside the hook (was module-level) so it can log through logError -
  // every call site shares this one validation-tracking point instead of
  // needing its own logError call.
  const assertDefined = <T>(v: T | undefined | null, msg: string): T => {
    if (v === undefined || v === null) {
      // Validation message: dynamic - see the msg argument at each call site
      // (e.g. "Origin missing", "Auth token missing", "Filters not ready in cache")
      logError(
        apiErrorMeta.ERROR_TRACKING_EVENTS.ERROR_TRACKING,
        errorTrackingEvent({
          errorType: 'validation',
          errorDescription: msg,
          errorMessage: msg,
          screenName,
          service: apiErrorMeta.SERVICE_NAMES.SEARCH_AGGREGATOR,
        }),
      );
      throw new Error(msg);
    }
    return v;
  };

  const { isLoggedIn } = useIsLoggedIn('FLIGHTS');
  const { multiCart } = useMultiCartContext();
  const { enabled: isSoldOutListingEnabled } = useIsFlagEnabled(
    'flights_soldout_listing_enabled',
  );
  const accessTokenQuery = useAccessToken('FLIGHTS');
  const authTokenQuery = useAuthToken(screenName);
  const { data: accessToken } = accessTokenQuery;
  const { data: authToken } = authTokenQuery;
  const { data: userID } = useUserId('FLIGHTS');
  const { data: refreshToken } = useRefreshToken('FLIGHTS');
  const anonymousSso =
    Platform.OS === 'web' ? flightConfig.config.anonymousSso : undefined;

  const flightApiEnv = flightConfig.config.env || appConfig.config.env;
  // always read latest filters from cache at mutation time
  const getFilters = (): SearchFlightFilters => {
    const cached = queryClient.getQueryData<SearchFlightFilters>(
      flightKeys.filters,
    );
    return assertDefined(cached, 'Filters not ready in cache');
  };

  const getFunnelControlResults = (): FunnelControlResponse | null => {
    const cached = queryClient.getQueryData<SearchAggregatorState>(
      flightKeys.search,
    );
    return cached?.data?.funnel ?? null;
  };

  const funnelService = useMemo(
    () =>
      new FunnelControlApiService(
        flightApiEnv as Environment,
        flightConfig.config.geoId,
      ),
    [flightApiEnv, flightConfig.config.geoId],
  );

  const searchService = useMemo(
    () =>
      new SearchAggregatorApiService(
        flightApiEnv as Environment,
        flightConfig.config.geoId,
      ),
    [flightApiEnv, flightConfig.config.geoId],
  );

  const buildFunnelPayload = (args: {
    userContext: UserContext;
    ssoDetails: SSODetails;
  }): FunnelControlRequest => {
    const f = getFilters();
    const { userContext, ssoDetails } = args;

    const originObj = assertDefined(f.originObj, 'Origin missing');
    const destinationObj = assertDefined(
      f.destinationObj,
      'Destination missing',
    );
    const departDate = assertDefined(f.departDate, 'Depart date missing');

    const origin = assertDefined(
      originObj.IsCity ? originObj.CityCode : originObj.StationCode,
      'Origin code missing',
    );

    const destination = assertDefined(
      destinationObj.IsCity
        ? destinationObj.CityCode
        : destinationObj.StationCode,
      'Destination code missing',
    );

    const isReturn = f.tripType !== TripType.ONE_WAY;
    const provider = getProvider(destinationObj);

    return {
      searchContext: {
        flightJourney: {
          journeyType: isReturn ? 'R' : 'O',
          journeyDetails: [
            {
              departDate: formatDateDDMMYYYY(departDate),
              returnDate:
                isReturn && f.returnDate
                  ? formatDateDDMMYYYY(f.returnDate)
                  : '',
              origin,
              destination,
              isOriginCity: Boolean(originObj.IsCity),
              isDestinationCity: Boolean(destinationObj.IsCity),
            },
          ],
          passengers: {
            adult: f.adultCount ?? 1,
            child: f.childCount ?? 0,
            infant: f.infantCount ?? 0,
          },
        },
        promocode: f.promoCode ?? null,
        cabinClass: f.cabinClass,
      },
      userContext: {
        ...userContext,
        ssoDetails,
      },
      inventoryContext: {
        airlineProfile: flightConfig?.config?.isDistressflights ? 'd' : null,
        type: null,
        provider: provider ?? 'AAB',
      },
      metaData: {
        dest: originObj.Dest ?? '',
      },
      ...(multiCart?.cartId && {
        cartContext: {
          cartId: multiCart.cartId,
          uiHide: multiCart.uiHide,
          uiDisable: multiCart.uiDisable,
          airasiaFlightOnly: multiCart.airasiaFlightOnly,
          sourceLob: multiCart.sourceLob,
          sourceDeeplink: multiCart.sourceDeeplink,
        },
      }),
    };
  };

  return useMutation<PipelineResult, Error, SearchAggregatorVars | void>({
    onMutate: () => {
      queryClient.setQueryData<SearchAggregatorState>(
        flightKeys.search,
        (prev) => ({
          status: 'loading',
          data: prev?.data ?? null, // keep last data if present
          error: null,
        }),
      );
    },

    mutationFn: async (vars) => {
      const safeVars = (vars ?? {}) as Partial<SearchAggregatorVars>;
      const {
        shouldCallFunnel = true,
        shouldCallAggregator = true,
        funnelData,
        locale,
        currency,
        showSoldOutFlights: showSoldOutFlightsOverride,
      } = safeVars;
      const controller = new AbortController();
      const signal = controller.signal;
      const filters = getFilters();
      // getToken() refreshes the access token if expired — check the returned
      // value, not accessTokenQuery.isTokenValid which is a stale render-time snapshot.
      const effectiveAccessToken = isLoggedIn
        ? await accessTokenQuery.getToken()
        : anonymousSso?.accessToken;
      const effectiveRefreshToken = isLoggedIn
        ? refreshToken
        : anonymousSso?.refreshToken;
      const effectiveUserId = isLoggedIn ? userID : anonymousSso?.userId;

      if (isLoggedIn && !effectiveAccessToken) {
        // Validation message: "Access token invalid"
        logError(
          apiErrorMeta.ERROR_TRACKING_EVENTS.ERROR_TRACKING,
          errorTrackingEvent({
            errorType: 'validation',
            errorDescription: 'Access token invalid',
            errorMessage: 'Access token invalid',
            screenName,
            service: apiErrorMeta.SERVICE_NAMES.SEARCH_AGGREGATOR,
          }),
        );
        throw new Error('Access token invalid');
      }
      if (!authTokenQuery.isTokenValid) {
        // Validation message: "Auth token invalid"
        logError(
          apiErrorMeta.ERROR_TRACKING_EVENTS.ERROR_TRACKING,
          errorTrackingEvent({
            errorType: 'validation',
            errorDescription: 'Auth token invalid',
            errorMessage: 'Auth token invalid',
            screenName,
            service: apiErrorMeta.SERVICE_NAMES.SEARCH_AGGREGATOR,
          }),
        );
        throw new Error('Auth token invalid');
      }
      if (!isLoggedIn && !hasValidAnonymousSso(anonymousSso)) {
        // Validation message: "Anonymous access token missing"
        logError(
          apiErrorMeta.ERROR_TRACKING_EVENTS.ERROR_TRACKING,
          errorTrackingEvent({
            errorType: 'validation',
            errorDescription: 'Anonymous access token missing',
            errorMessage: 'Anonymous access token missing',
            screenName,
            service: apiErrorMeta.SERVICE_NAMES.SEARCH_AGGREGATOR,
          }),
        );
        throw new Error('Anonymous access token missing');
      }

      const bearer = assertDefined(authToken, 'Auth token missing');
      const loggedInUserAccessToken = assertDefined(
        effectiveAccessToken,
        'Access Token missing',
      );
      const loggedInUserRefreshToken = assertDefined(
        effectiveRefreshToken,
        'Refresh Token missing',
      );
      const loggedInUserId = assertDefined(effectiveUserId, 'User ID missing');

      const funnelHeaders: FunnelHeadersInput = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        channel_hash:
          multiCart?.channelHash || flightConfig.config.channelHash!,
        'Ga-Id': flightConfig.config.gaId,
      };

      const searchAggregatorHeaders: SearchAggregatorInput = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        channel_hash:
          multiCart?.channelHash || flightConfig.config.channelHash!,
        'User-Type': isLoggedIn ? 'MEMBER' : 'ANONYMOUS',
        Authorization: `Bearer ${bearer}`,
      };
      const showSoldOutFlights =
        showSoldOutFlightsOverride ??
        (getBooleanQueryParam('showSoldOutFlights') || isSoldOutListingEnabled);
      const userContext: UserContext = {
        currency: (
          currency ||
          flightConfig?.config?.currency ||
          appConfig?.config?.currency
        ).toUpperCase(),
        geoId: flightConfig.config.geoId?.toUpperCase() ?? 'MY',
        locale: locale ?? appConfig.config.locale ?? 'en-gb',
        platform: getApiPlatform(),
        ...(Platform.OS !== 'web' && { version: appConfig.config.appVersion }),
      };

      const ssoDetails: SSODetails = {
        accessToken: loggedInUserAccessToken,
        refreshToken: loggedInUserRefreshToken,
        userId: loggedInUserId,
      };

      let funnel: FunnelControlResponse | null = null;

      if (shouldCallFunnel) {
        // 1) Funnel must succeed
        funnel = await funnelService.fetchFunnelControl(
          buildFunnelPayload({ userContext, ssoDetails }),
          funnelHeaders,
          signal,
        );

        queryClient.setQueryData<SearchAggregatorState>(
          flightKeys.search,
          (prev) => ({
            status: 'loading',
            data: {
              funnel,
              aggregator: null,
            },
            error: null,
          }),
        );
      } else if (funnelData) {
        funnel = funnelData;
      } else {
        // 2) Use cached funnel
        funnel = getFunnelControlResults();
        if (!funnel) {
          // Validation message: "Funnel not available in cache"
          logError(
            apiErrorMeta.ERROR_TRACKING_EVENTS.ERROR_TRACKING,
            errorTrackingEvent({
              errorType: 'validation',
              errorDescription: 'Funnel cache miss',
              errorMessage: 'Funnel not available in cache',
              screenName,
              service: apiErrorMeta.SERVICE_NAMES.SEARCH_AGGREGATOR,
            }),
          );
          throw new Error('Funnel not available in cache');
        }
      }

      // Just makes Funnel Call
      if (!shouldCallAggregator) {
        return {
          funnel,
          aggregator: null,
        };
      }

      // 2) Aggregator only after funnel success
      const filterParams = buildAggregatorFilterParams({
        filters,
        funnel,
        consumerId: flightConfig.config.consumerId,
      });
      const queryParams = buildAggregatorQueryParams({
        filterParams,
        funnel,
        showSoldOutFlights,
        isDistress: !!flightConfig?.config?.isDistressflights,
      });
      const body = buildAggregatorBody({
        filters,
        filterParams,
        userContext,
        ssoDetails,
      });
      const aggregator = await searchService.fetchAggregatedResults({
        queryParams,
        body,
        headers: searchAggregatorHeaders,
        signal,
        lob: multiCart?.sourceLob,
      });

      return { funnel, aggregator };
    },

    onSuccess: (data) => {
      queryClient.setQueryData<SearchAggregatorState>(flightKeys.search, {
        status: 'success',
        data,
        error: null,
      });
    },

    onError: (error) => {
      queryClient.setQueryData<SearchAggregatorState>(
        flightKeys.search,
        (prev) => ({
          status: 'error',
          data: prev?.data ?? null,
          error,
        }),
      );

      const { errorType, errorCode, errorDescription, errorMessage } =
        classifyServiceError(error);

      logError(
        apiErrorMeta.ERROR_TRACKING_EVENTS.ERROR_TRACKING,
        errorTrackingEvent({
          errorType,
          errorCode,
          errorDescription,
          errorMessage,
          screenName,
          service: apiErrorMeta.SERVICE_NAMES.SEARCH_AGGREGATOR,
        }),
      );
    },
  });
};
